import React, { useState, useRef, useEffect } from "react";
import {
    Card,
    Grid,
    TextField,
    Typography,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Icon,
    Box,
    CircularProgress,
    IconButton,
    Backdrop,
    Chip,
    Autocomplete,
    Checkbox
} from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { CloudUpload, Close, Email, Delete, Visibility, Send, RestartAlt, History, CheckCircle, Error as ErrorIcon, Warning } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import BroadcastHistory from "./BroadcastHistory";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { compressImage } from "utils/imageCompression";



const BroadcastEmail = () => {

    const { data: user, isLoading: userLoading } = useQuery({
        queryKey: ["auth-me"],
        queryFn: async () => {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/me`, {}, { withCredentials: true });
            return response.data;
        },
        retry: false,
    });
    const [showHistory, setShowHistory] = useState(false);

    // Fellows (non-admins) must always see history and cannot compose
    useEffect(() => {
        if (!userLoading && user && user.role_id !== 1 && user.role_id !== 2) {
            setShowHistory(true);
        }
    }, [user, userLoading]);

    const [loading, setLoading] = useState(false);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
    const [previewOpen, setPreviewOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmResetOpen, setConfirmResetOpen] = useState(false);
    const fileInputRef = useRef(null);

    const { data: broadcastUsers, isLoading: usersLoading } = useQuery({
        queryKey: ["broadcast-users"],
        queryFn: async () => {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/email/users`, { withCredentials: true });
            return response.data.data;
        },
    });

    const formik = useFormik({
        initialValues: {
            subject: "",
            message: "",
            attachments: [],
            sent_to: [], // Multi-select user IDs
        },
        validationSchema: Yup.object({
            subject: Yup.string()
                .trim()
                .required("Subject is required")
                .max(150, "Subject cannot exceed 150 characters"),
            message: Yup.string()
                .trim()
                .required("Message body is required")
                .min(10, "Message must be at least 10 characters long"),
            sent_to: Yup.array()
                .min(1, "Please select at least one recipient")
                .required("Recipients are required"),
            attachments: Yup.array()
                .of(
                    Yup.object().shape({
                        url: Yup.string().required(),
                        name: Yup.string().required(),
                        type: Yup.string().required(),
                        size: Yup.number().required()
                    })
                ),
        }),
        onSubmit: async (values) => {
            setConfirmOpen(true);
        },
    });

    if (userLoading) {
        return (
            <DashboardLayout>
                <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
                    <CircularProgress color="info" />
                </MDBox>
            </DashboardLayout>
        );
    }

    const handleSendBroadcast = async () => {
        setConfirmOpen(false);
        setLoading(true);

        try {
            const broadcastData = {
                subject: formik.values.subject,
                message: formik.values.message,
                attachments: formik.values.attachments.map(file => ({
                    url: file.url,
                    name: file.name
                })),
                sent_to: formik.values.sent_to
            };

            // console.log(broadcastData);

            await axios.post(
                `${process.env.REACT_APP_API_URL}/email/broadcast`,
                broadcastData,
                { withCredentials: true }
            );

            const isAllSelected = formik.values.sent_to.includes("ALL");
            const recipientText = isAllSelected ? "all users" : `${formik.values.sent_to.length} selected user(s)`;

            setNotification({
                open: true,
                message: `Email broadcasted successfully to ${recipientText}`,
                severity: "success",
            });
            formik.resetForm();
            setShowHistory(true);
        } catch (error) {
            console.error("Broadcast failed", error);
            setNotification({
                open: true,
                message: error.response?.data?.message || "Failed to broadcast email. Please try again.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.currentTarget.files || []);
        if (files.length === 0) return;

        const currentAttachments = formik.values.attachments;

        const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];
        const newAttachments = [];

        setIsUploadingFile(true);
        try {
            for (const file of files) {
                if (!allowedTypes.includes(file.type) &&
                    !file.name.toLowerCase().endsWith('.doc') &&
                    !file.name.toLowerCase().endsWith('.docx')) {
                    setNotification({
                        open: true,
                        message: `${file.name}: Only PDF, Word, and Image files (JPG, PNG) are allowed`,
                        severity: "error"
                    });
                    continue;
                }

                // Upload file
                const compressedFile = await compressImage(file);
                const formData = new FormData();
                formData.append("file", compressedFile);

                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/reports/upload`,
                    formData,
                    { withCredentials: true }
                );

                newAttachments.push({
                    url: response.data.url,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    lastModified: Date.now()
                });
            }

            // Add new attachments to existing ones
            if (newAttachments.length > 0) {
                formik.setFieldValue("attachments", [...currentAttachments, ...newAttachments]);
                setNotification({
                    open: true,
                    message: `${newAttachments.length} file(s) uploaded successfully`,
                    severity: "success"
                });
            }

            // Reset file input after upload
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (error) {
            console.error("Upload failed", error);
            setNotification({
                open: true,
                message: error.response?.data?.message || "File upload failed. Please try again.",
                severity: "error"
            });
            if (fileInputRef.current) fileInputRef.current.value = "";
        } finally {
            setIsUploadingFile(false);
        }
    };

    const handleReset = () => {
        formik.resetForm();
        // FIX: Reset file input when clearing form
        if (fileInputRef.current) fileInputRef.current.value = "";
        setConfirmResetOpen(false);
    };

    const handleEdit = (row) => {
        let attachments = [];
        let sentTo = [];
        try {
            attachments = typeof row.attachments === "string" ? JSON.parse(row.attachments) : row.attachments;
            // Map attachments to include a default type for the UI if missing
            attachments = (attachments || []).map(file => {
                const lowerUrl = (file.url || "").toLowerCase();
                let type = "image/jpeg";
                if (lowerUrl.endsWith(".pdf")) type = "application/pdf";
                else if (lowerUrl.endsWith(".doc") || lowerUrl.endsWith(".docx")) type = "application/msword";

                return {
                    ...file,
                    type: type,
                    size: 0 // We don't have size in DB
                };
            });
        } catch (e) {
            attachments = [];
        }

        try {
            sentTo = typeof row.sent_to === "string" ? JSON.parse(row.sent_to) : row.sent_to;
            if (!Array.isArray(sentTo)) sentTo = [];
        } catch (e) {
            sentTo = [];
        }

        formik.setValues({
            subject: row.subject || "",
            message: row.message || "",
            attachments: attachments || [],
            sent_to: sentTo || []
        });
        setShowHistory(false);
    };

    const handleRemoveAttachment = (index) => {
        const newAttachments = formik.values.attachments.filter((_, i) => i !== index);
        formik.setFieldValue("attachments", newAttachments);
    };

    return (
        <DashboardLayout>
            <MDBox pb={3}>
                <Grid item xs={12}>
                    <Card>
                        <MDBox
                            mx={2}
                            mt={2}
                            mb={2}
                            py={3}
                            px={2}
                            variant="gradient"
                            bgColor="info"
                            borderRadius="lg"
                            coloredShadow="info"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                <MDTypography variant="h6" color="white">
                                    {showHistory ? "Broadcast History" : "Broadcast Email"}
                                </MDTypography>
                            </Box>
                            {(user?.role_id === 1 || user?.role_id === 2) && (
                                <MDButton
                                    variant="outlined"
                                    color="white"
                                    size="small"
                                    startIcon={<Icon>{showHistory ? "add" : "history"}</Icon>}
                                    onClick={() => setShowHistory(!showHistory)}
                                >
                                    {showHistory ? "Compose Email" : "History"}
                                </MDButton>
                            )}
                        </MDBox>

                        {(!showHistory && (user?.role_id === 1 || user?.role_id === 2)) ? (
                            <MDBox p={3}>
                                <form onSubmit={formik.handleSubmit}>
                                    <MDBox mb={2}>
                                        <Autocomplete
                                            multiple
                                            id="sent_to"
                                            options={[
                                                { user_id: "ALL", user_name: "All Users", district_name: "" },
                                                ...(broadcastUsers || [])
                                            ]}
                                            disableCloseOnSelect
                                            getOptionLabel={(option) =>
                                                option.user_id === "ALL"
                                                    ? "All Users"
                                                    : `${option.user_name} (${option.district_name || "N/A"})`
                                            }
                                            value={
                                                (formik.values.sent_to || []).includes("ALL")
                                                    ? [{ user_id: "ALL", user_name: "All Users", district_name: "" }]
                                                    : (broadcastUsers || []).filter((u) =>
                                                        u && (formik.values.sent_to || []).includes(u.user_id)
                                                    )
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                option && value && option.user_id === value.user_id
                                            }
                                            onChange={(event, newValue) => {
                                                if (!newValue) {
                                                    formik.setFieldValue("sent_to", []);
                                                    return;
                                                }

                                                const currentSentTo = formik.values.sent_to || [];
                                                const wasAllSelected = currentSentTo.includes("ALL");
                                                const isAllOptionInNewValue = newValue.some(item => item && item.user_id === "ALL");

                                                if (!wasAllSelected && isAllOptionInNewValue) {
                                                    // "All Users" was toggled ON
                                                    formik.setFieldValue("sent_to", ["ALL"]);
                                                } else if (wasAllSelected && !isAllOptionInNewValue) {
                                                    // "All Users" was toggled OFF directly
                                                    formik.setFieldValue("sent_to", []);
                                                } else if (wasAllSelected && isAllOptionInNewValue && newValue.length > 1) {
                                                    // Handle deselecting an individual while "ALL" is active
                                                    const individualClicked = newValue.find(item => item && item.user_id !== "ALL");
                                                    if (individualClicked) {
                                                        const allExceptClicked = (broadcastUsers || [])
                                                            .filter(u => u && u.user_id !== individualClicked.user_id)
                                                            .map(u => u.user_id);
                                                        formik.setFieldValue("sent_to", allExceptClicked);
                                                    }
                                                } else {
                                                    // Normal individual selections
                                                    const selectedIds = newValue
                                                        .filter(item => item && item.user_id !== "ALL")
                                                        .map(u => u.user_id);

                                                    if (broadcastUsers && selectedIds.length === broadcastUsers.length && selectedIds.length > 0) {
                                                        formik.setFieldValue("sent_to", ["ALL"]);
                                                    } else {
                                                        formik.setFieldValue("sent_to", selectedIds);
                                                    }
                                                }
                                            }}
                                            renderOption={(props, option, { selected }) => {
                                                if (!option) return null;
                                                const currentSentTo = formik.values.sent_to || [];
                                                const isAllSelectedInState = currentSentTo.includes("ALL");
                                                const isItemSelected = selected ||
                                                    (option.user_id === "ALL" && (isAllSelectedInState || (broadcastUsers && currentSentTo.length === (broadcastUsers?.length || 0) && (broadcastUsers?.length || 0) > 0))) ||
                                                    (isAllSelectedInState && option.user_id !== "ALL");

                                                return (
                                                    <li {...props} key={option.user_id}>
                                                        <Checkbox
                                                            icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                            checkedIcon={<CheckBox fontSize="small" />}
                                                            style={{ marginRight: 8 }}
                                                            checked={isItemSelected}
                                                        />
                                                        {option.user_id === "ALL"
                                                            ? "All Users"
                                                            : `${option.user_name} (${option.district_name || "N/A"})`}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        <span>
                                                            Select Recipients <span style={{ color: "red" }}>*</span>
                                                        </span>
                                                    }
                                                    placeholder="Search users..."
                                                    error={formik.touched.sent_to && Boolean(formik.errors.sent_to)}
                                                    helperText={formik.touched.sent_to && formik.errors.sent_to}
                                                />
                                            )}
                                            loading={usersLoading}
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <TextField
                                            fullWidth
                                            label={<span>Email Subject <span style={{ color: "red" }}>*</span></span>}
                                            name="subject"
                                            variant="outlined"
                                            placeholder="Enter email subject"
                                            value={formik.values.subject}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.subject && Boolean(formik.errors.subject)}
                                            helperText={(formik.touched.subject && formik.errors.subject) || `${formik.values.subject.length}/150`}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <Typography variant="button" color="text" fontWeight="medium">
                                            Email Body <span style={{ color: "red" }}>*</span>
                                        </Typography>
                                        <Box sx={{
                                          mt: 1,
                                          '& .quill': { borderRadius: '8px', border: formik.errors.message ? '1px solid red' : '1px solid #ccc' },
                                          '& .ql-container': { minHeight: 250, fontSize: '1rem', color: '#1a237e' },
                                          '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8, border: '1px solid #ccc' },
                                          '& .ql-container.ql-snow': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, border: '1px solid #ccc', borderTop: 'none' }
                                        }}>
                                            <ReactQuill
                                                theme="snow"
                                                value={formik.values.message}
                                                onChange={(value) => {
                                                    formik.setFieldValue('message', value);
                                                    if (!formik.touched.message) {
                                                        formik.setFieldTouched('message', true, false);
                                                    }
                                                }}
                                                placeholder="Write your message here..."
                                                modules={{
                                                    toolbar: [
                                                        ['bold', 'italic', 'underline', 'strike'],
                                                        ['link'],
                                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                        ['clean']
                                                    ]
                                                }}
                                            />
                                        </Box>
                                        {formik.touched.message && formik.errors.message && (
                                            <MDTypography variant="caption" color="error">{formik.errors.message}</MDTypography>
                                        )}
                                    </MDBox>

                                    <MDBox mb={3}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                            <MDTypography variant="button" fontWeight="regular" color="text">
                                                Attachments (Optional)
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text">
                                                {formik.values.attachments.length} file(s) attached
                                            </MDTypography>
                                        </MDBox>

                                        <MDBox
                                            mt={1}
                                            p={2}
                                            border="1px dashed"
                                            borderColor="grey.400"
                                            borderRadius="lg"
                                        >
                                            <input
                                                ref={fileInputRef}
                                                id="raised-button-file"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                type="file"
                                                multiple
                                                hidden
                                                onChange={handleFileChange}
                                                disabled={isUploadingFile}
                                                aria-label="Upload attachment files"
                                            />

                                            <label
                                                htmlFor="raised-button-file"
                                                style={{
                                                    width: "100%",
                                                    cursor: isUploadingFile ? "not-allowed" : "pointer"
                                                }}
                                            >
                                                <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                                                    {isUploadingFile ? (
                                                        <CircularProgress size={24} />
                                                    ) : (
                                                        <CloudUpload color="action" />
                                                    )}
                                                    <MDTypography variant="button" color="text" textAlign="center">
                                                        {isUploadingFile ? "Uploading..." : "Click or drag files here"}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="text" opacity={0.7}>
                                                        PDF, Word, JPG, PNG
                                                    </MDTypography>
                                                </MDBox>
                                            </label>

                                            {formik.values.attachments.length > 0 && (
                                                <MDBox mt={2} display="flex" flexWrap="wrap" gap={1}>
                                                    {formik.values.attachments.map((file, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                bgcolor: "grey.100",
                                                                borderRadius: 1,
                                                                px: 1.5,
                                                                py: 0.5,
                                                                border: "1px solid",
                                                                borderColor: "grey.300"
                                                            }}
                                                        >
                                                            <Icon sx={{ fontSize: 16 }}>
                                                                {file.type === "application/pdf" ? "picture_as_pdf" :
                                                                    (file.type.includes("word") || file.name.toLowerCase().endsWith(".doc") || file.name.toLowerCase().endsWith(".docx")) ? "description" : "image"}
                                                            </Icon>
                                                            <Typography variant="caption" sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {file.name}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveAttachment(index)}
                                                                aria-label={`Remove ${file.name}`}
                                                                sx={{ ml: 0.5, p: 0.25 }}
                                                            >
                                                                <Close sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </MDBox>
                                            )}
                                        </MDBox>
                                        {formik.errors.attachments && (
                                            <MDTypography variant="caption" color="error">
                                                {formik.errors.attachments}
                                            </MDTypography>
                                        )}
                                    </MDBox>

                                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                                        <MDButton
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<RestartAlt />}
                                            onClick={() => {
                                                if (formik.dirty) setConfirmResetOpen(true);
                                            }}
                                            disabled={!formik.dirty}
                                        >
                                            Clear
                                        </MDButton>
                                        <Box display="flex" gap={2}>
                                            <MDButton
                                                variant="outlined"
                                                color="info"
                                                startIcon={<Visibility />}
                                                onClick={() => setPreviewOpen(true)}
                                                disabled={!formik.isValid || !formik.dirty}
                                            >
                                                Preview
                                            </MDButton>
                                            <MDButton
                                                type="submit"
                                                variant="gradient"
                                                color="info"
                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                                disabled={!formik.isValid || !formik.dirty || loading || isUploadingFile}
                                            >
                                                {loading ? "Sending..." : "Broadcast"}
                                            </MDButton>
                                        </Box>
                                    </MDBox>
                                </form>
                            </MDBox>
                        ) : (
                            <BroadcastHistory onEdit={handleEdit} />
                        )}
                    </Card>
                </Grid>

                {/* Preview Dialog */}
                <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            Email Preview
                            <IconButton onClick={() => setPreviewOpen(false)} aria-label="close preview">
                                <Close />
                            </IconButton>
                        </MDBox>
                    </DialogTitle>
                    <DialogContent dividers>
                        <MDTypography variant="button" fontWeight="bold" color="text" display="block" gutterBottom>
                            Subject: {formik.values.subject}
                        </MDTypography>
                        <Divider />
                        <Box sx={{ mt: 2, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                            <div dangerouslySetInnerHTML={{ __html: formik.values.message }} />
                        </Box>
                        {formik.values?.attachments?.length > 0 && (
                            <Box mt={3}>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                    Attachments ({formik.values.attachments.length}):
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {formik.values.attachments.map((file, index) => (
                                        <Box key={index} p={1} bgcolor="grey.100" borderRadius={1} display="inline-flex" alignItems="center" gap={1}>
                                            <Icon>
                                                {file.type === "application/pdf" ? "picture_as_pdf" :
                                                    (file.type.includes("word") || file.name.toLowerCase().endsWith(".doc") || file.name.toLowerCase().endsWith(".docx")) ? "description" : "image"}
                                            </Icon>
                                            <Typography variant="caption">{file.name}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton color="secondary" onClick={() => setPreviewOpen(false)}>Close</MDButton>
                    </DialogActions>
                </Dialog>

                {/* Confirmation Dialog */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>Confirm Broadcast</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {formik.values.sent_to.includes("ALL")
                                ? "Are you sure you want to send this email to all users?"
                                : `Are you sure you want to send this email to the ${formik.values.sent_to.length} selected user(s)?`}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <MDButton color="secondary" onClick={() => setConfirmOpen(false)}>Cancel</MDButton>
                        <MDButton color="info" onClick={handleSendBroadcast} autoFocus>Yes, Send Broadcast</MDButton>
                    </DialogActions>
                </Dialog>

                {/* Reset Confirmation Dialog */}
                <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)}>
                    <DialogTitle>Clear Form</DialogTitle>
                    <DialogContent>
                        <Typography>Are you sure you want to clear all fields?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <MDButton color="secondary" onClick={() => setConfirmResetOpen(false)}>Cancel</MDButton>
                        <MDButton color="error" onClick={handleReset} autoFocus>Yes, Clear All</MDButton>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={notification.open}
                    autoHideDuration={6000}
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity={notification.severity} variant="filled" sx={{ width: "100%", color: "white" }}>
                        {notification.message}
                    </Alert>
                </Snackbar>

                {/* Backdrop for Loading State */}
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backdropFilter: 'blur(3px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    }}
                    open={loading}
                >
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <CircularProgress color="inherit" size={60} />
                        <Typography variant="h6" color="white">
                            Sending broadcast email...
                        </Typography>
                        <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                            Please wait while we send your message to {formik.values.sent_to.includes("ALL") ? "all users" : "the selected user(s)"}
                        </Typography>
                    </Box>
                </Backdrop>
            </MDBox>
        </DashboardLayout>
    );
};

export default BroadcastEmail;